import { PAGE_SIZE_MAX } from "@seagames/contracts";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { ContingentView, type ContingentSearch } from "../../components/accommodation/contingent";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { ROOMING_PERSON_STATES } from "../../lib/accommodation/types";
import { canManageAccommodation, canSeeHostAccommodation, canUseDesk } from "../../lib/accommodation/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { searchText } from "../../lib/list-page/list-search";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * One contingent's rooming list (her `#/accn/<NOC>`): a full page with the
 * back arrow (the layout drops the tabs). URL = the list's state in the
 * API's keys, every param lenient. `card` (GMS-2, "Assign room" from the
 * Overview's arrivals without a room or a record): that person's sheet opens
 * once their row is on the page (`q` = their card number puts it there).
 */
export const Route = createFileRoute("/_app/accommodation/rooming/$orgId")({
	validateSearch: z.object({
		state: lenient(z.enum(ROOMING_PERSON_STATES)),
		gender: lenient(z.enum(["MALE", "FEMALE"])),
		lodging_building_id: lenient(z.string().uuid()),
		q: lenient(searchText()),
		sort: lenient(z.enum(["name", "team", "room"])),
		page: lenient(z.coerce.number().int().min(1)),
		page_size: lenient(z.coerce.number().int().min(1).max(PAGE_SIZE_MAX)),
		card: lenient(z.string().uuid()),
	}),
	beforeLoad: ({ context }) => {
		if (!canSeeHostAccommodation(context.session)) throw redirect({ to: "/accommodation" });
	},
	component: ContingentPage,
});

function ContingentPage() {
	const { session } = Route.useRouteContext();
	const { orgId } = Route.useParams();
	const search = Route.useSearch() as ContingentSearch;
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	if (!gamesId) return <PageSkeleton />;
	return (
		<ContingentView
			key={orgId}
			gamesId={gamesId}
			orgId={orgId}
			search={search}
			patch={(next) => void navigate({ search: (prev) => ({ ...prev, page: undefined, ...next }), replace: true })}
			onFocusShown={() => void navigate({ search: (prev) => ({ ...prev, card: undefined }), replace: true })}
			canManage={canManageAccommodation(session)}
			canDesk={canUseDesk(session)}
		/>
	);
}
